// React Query hooks for the WB vehicles admin surface (Phase C3 Group 2).
//
// Pairs with /api/v1/wb/vehicles/* — list (paginated + filterable) /
// search (typeahead) / detail / create / patch / delete plus the stored
// tare set+clear endpoints. `enc_id` is the encrypted scalar id
// (snake_case throughout the WB module). DTOs mirror
// server/src/modules/wb/vehicles/types.ts exactly.
//
// Tare is intentionally separated from the main update surface — the
// server tracks `stored_tare_set_at` / `stored_tare_set_by` and computes
// a `tare_status` ('not_set' | 'stale' | 'fresh') against
// `wb_company_settings.stale_tare_threshold_days`. Setting tare goes
// through POST /:enc_id/tare; clearing it through DELETE /:enc_id/tare.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbVehicleDto on the server) -----------------

export type VehicleType = 'rigid' | 'artic' | 'van' | 'skip' | 'roro' | 'other';
export type TareStatus = 'not_set' | 'stale' | 'fresh';

export interface WbVehicle {
  readonly enc_id: string;
  readonly uuid: string;
  readonly registration: string;
  readonly haulier: { readonly enc_id: string; readonly name: string } | null;
  readonly vehicle_type: VehicleType;
  readonly max_capacity_kg: number | null;
  readonly stored_tare_kg: number | null;
  readonly stored_tare_set_at: string | null;
  readonly tare_status: TareStatus;
  readonly notes: string | null;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface Pagination {
  readonly page: number;
  readonly per_page: number;
  readonly total: number;
  readonly total_pages: number;
}

export interface CreateVehicleInput {
  registration: string;
  haulier_id?: string | null;
  vehicle_type?: VehicleType;
  max_capacity_kg?: number | null;
  notes?: string | null;
  is_active?: boolean;
}

export type UpdateVehicleInput = Partial<CreateVehicleInput>;

export interface SetTareInput {
  weight_kg: number;
  reason?: string;
}

export interface ListVehiclesParams {
  page?: number;
  per_page?: number;
  registration?: string;
  /** Deprecated alias — use `haulier_enc_id` for new code. */
  haulier_id?: string;
  haulier_enc_id?: string;
  is_active?: boolean;
}

interface ListVehiclesResponse {
  vehicles: WbVehicle[];
  pagination: Pagination;
}

interface SearchVehiclesResponse {
  vehicles: WbVehicle[];
}

// -- Helpers ----------------------------------------------------------------

function buildQueryString(params: ListVehiclesParams): string {
  const sp = new URLSearchParams();
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.per_page !== undefined) sp.set('per_page', String(params.per_page));
  if (params.registration !== undefined && params.registration !== '') {
    sp.set('registration', params.registration);
  }
  if (params.haulier_id !== undefined && params.haulier_id !== '') {
    sp.set('haulier_id', params.haulier_id);
  }
  if (params.haulier_enc_id !== undefined && params.haulier_enc_id !== '') {
    sp.set('haulier_enc_id', params.haulier_enc_id);
  }
  if (params.is_active !== undefined) sp.set('is_active', String(params.is_active));
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

// -- Queries ----------------------------------------------------------------

export function useWbVehicles(params: ListVehiclesParams = {}) {
  return useQuery<ListVehiclesResponse>({
    queryKey: queryKeys.wb.vehicles(params as Record<string, unknown>),
    queryFn: async () => {
      const qs = buildQueryString(params);
      const res = await apiClient.get<ListVehiclesResponse>(
        `${endpoints.wb.vehicles}${qs}`,
      );
      return res.data;
    },
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

/** Typeahead — server returns up to 10 partial-match hits. Disabled until
 *  the query string is at least 2 characters. Short staleTime keeps
 *  results fresh while the user is still typing. */
export function useWbVehiclesSearch(registration: string) {
  const trimmed = registration.trim();
  const enabled = trimmed.length >= 2;
  return useQuery<SearchVehiclesResponse>({
    queryKey: queryKeys.wb.vehiclesSearch(trimmed),
    queryFn: async () => {
      const qs = new URLSearchParams({ registration: trimmed }).toString();
      const res = await apiClient.get<SearchVehiclesResponse>(
        `${endpoints.wb.vehiclesSearch}?${qs}`,
      );
      return res.data;
    },
    enabled,
    staleTime: 10_000,
  });
}

export function useWbVehicle(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<{ vehicle: WbVehicle }>({
    queryKey: queryKeys.wb.vehicle(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbVehicle called without enc_id');
      const res = await apiClient.get<{ vehicle: WbVehicle }>(
        endpoints.wb.vehicle(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 15 * 60 * 1000, // Server caches for 30min; 15min client staleTime is safe
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation<{ vehicle: WbVehicle }, Error, CreateVehicleInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ vehicle: WbVehicle }, CreateVehicleInput>(
        endpoints.wb.vehicles,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicles() });
    },
  });
}

export function useUpdateVehicle(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ vehicle: WbVehicle }, Error, UpdateVehicleInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<{ vehicle: WbVehicle }, UpdateVehicleInput>(
        endpoints.wb.vehicle(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicles() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicle(encId) });
    },
  });
}

export function useDeleteVehicle(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.vehicle(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicles() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicle(encId) });
    },
  });
}

export function useSetVehicleTare(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ vehicle: WbVehicle }, Error, SetTareInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ vehicle: WbVehicle }, SetTareInput>(
        endpoints.wb.vehicleTare(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicles() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicle(encId) });
    },
  });
}

export function useClearVehicleTare(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ vehicle: WbVehicle }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ vehicle: WbVehicle }>(
        endpoints.wb.vehicleTare(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicles() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.vehicle(encId) });
    },
  });
}
